<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from 'vue'
import { FileUpload, useFileUpload } from '@ark-ui/vue'
type FileUploadFileError = 'TOO_MANY_FILES' | 'FILE_INVALID_TYPE' | 'FILE_TOO_LARGE' | 'FILE_TOO_SMALL' | 'FILE_INVALID' | 'FILE_EXISTS'
import UiFieldContainer from './UiFieldContainer.vue'
import UiIcon from './UiIcon.vue'
import UiButton from './UiButton/index'
import { useMediaUpload } from '../composables/useMediaUpload'

export interface UiFileUploadProps {
    name?: string
    id?: string
    // Accept can be a mime string like "image/*" or a map per Ark UI
    accept?: Record<string, string[]> | string | string[]
    maxFiles?: number
    maxFileSize?: number
    minFileSize?: number
    disabled?: boolean
    size?: 'md' | 'lg'
    hint?: string
    error?: string
    invalid?: boolean
    // Dropzone copy
    primaryText?: string
    secondaryText?: string
    // Preview aspect ratio, e.g. '16:9', '4/3', or numeric (1 for square)
    aspectRatio?: string | number
    // Optional convenience for pre-populating url items
    initialUrls?: string[]
    // Optional for pre-populating with full media items (includes media_id, etc.)
    initialItems?: UiFileUploadItem[]
    // Media upload props
    uploadFn?: (file: File, collection: string, metadata?: object) => Promise<{ id: string; url: string; filename?: string; metadata?: Record<string, unknown> }>
    deleteFn?: (item: UiFileUploadItem) => Promise<void>
    collection?: string
    metadata?: object
    autoUpload?: boolean
    refreshInterval?: number // in milliseconds, default 24 hours
    showThumbnails?: boolean
}

const props = withDefaults(defineProps<UiFileUploadProps>(), {
    size: 'md',
    maxFiles: 1,
    primaryText: 'Upload images',
    secondaryText: 'Drag & drop or click to upload',
    aspectRatio: '16:9',
    autoUpload: false,
    collection: 'default',
    refreshInterval: 86400000, // 24 hours
    showThumbnails: true,
})

export interface UiFileUploadItem { 
    file?: File; 
    url?: string; 
    media_id?: string;
    filename?: string;
    metadata?: Record<string, unknown>;
    uploadedAt?: Date | string;
    isTemporary?: boolean;
}

const model = defineModel<UiFileUploadItem[]>({ default: [] })

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const acceptedFiles = computed(() => fileUploadApi.value.acceptedFiles)
const errorMsg = ref<string | undefined>()

const fileUploadApi = useFileUpload({
    accept: props.accept as any,
    maxFiles: props.maxFiles,
    maxFileSize: props.maxFileSize,
    minFileSize: props.minFileSize,
    disabled: props.disabled,
    name: props.name,
    id: props.id || props.name,
    onFileReject,
    onFileAccept: () => { errorMsg.value = undefined },
})

const mediaUpload = props.autoUpload ? useMediaUpload({
    uploadFn: props.uploadFn,
    deleteFn: props.deleteFn,
    collection: props.collection,
    metadata: props.metadata,
    refreshInterval: props.refreshInterval,
}) : null

const loadedUrls = ref<Set<string>>(new Set())

function isUrlLoaded(url: string): boolean {
    return loadedUrls.value.has(url)
}

function filesSignature(files: File[]): string {
    return files.map(f => `${f.name}-${f.size}-${(f as any).lastModified ?? ''}`).join('|')
}

watch(acceptedFiles, async (files) => {
    if (!files) return
    
    const urlItems = (model.value || []).filter(i => i.url && !i.file)
    if (props.autoUpload && mediaUpload && files.length > 0) {
        await mediaUpload.handleFileSelection(files, (uploadedItems) => {
            model.value = [...urlItems, ...uploadedItems]
        })
    } else {
        const currentFileItems = (model.value || []).filter(i => i.file).map(i => i.file as File)
        if (filesSignature(files) === filesSignature(currentFileItems)) return
        model.value = [...urlItems, ...files.map(file => ({ file }))]
    }
})

watch(model, (items, oldItems) => {
    if (!items) return
    const files = items.filter(i => i.file).map(i => i.file as File)
    if (filesSignature(files) !== filesSignature(acceptedFiles.value)) {
        fileUploadApi.value.setFiles(files)
    }
    if ((props.maxFiles ?? Infinity) > (model.value?.length || 0)) {
        errorMsg.value = undefined
    }
    if (items.length === 0 && oldItems && oldItems.length > 0) {
        nextTick(() => {
            fileUploadApi.value.clearFiles()
            errorMsg.value = undefined
        })
    }
    emit('validate')
}, { immediate: true, deep: true })

const describedByBase = computed(() => props.id || props.name)

const remainingSlots = computed(() => {
    if (props.maxFiles == null) return Infinity
    const left = props.maxFiles - (model.value?.length || 0)
    return left < 0 ? 0 : left
})

const isMaxed = computed(() => {
    if (props.maxFiles == null) return false
    return remainingSlots.value === 0
})

const counterText = computed(() => {
    if (props.maxFiles == null) return undefined
    return `${model.value?.length || 0}/${props.maxFiles}`
})

function onFileReject(details: any) {
    const rejected = (details && (details.rejectedFiles || details.files || [])) as Array<{ file: File; errors: FileUploadFileError[] }>
    if (!rejected.length) return
    const errorSet = new Set<FileUploadFileError>(rejected.flatMap(r => r.errors))
    if (errorSet.has('TOO_MANY_FILES') && props.maxFiles != null) {
        errorMsg.value = `You can only upload up to ${props.maxFiles} images.`
        return
    }
    if (errorSet.has('FILE_INVALID_TYPE')) {
        errorMsg.value = 'Invalid file type.'
        return
    }
    if (errorSet.has('FILE_TOO_LARGE')) {
        errorMsg.value = 'File too large.'
        return
    }
    if (errorSet.has('FILE_TOO_SMALL')) {
        errorMsg.value = 'File too small.'
        return
    }
    errorMsg.value = 'Some files were rejected.'
}

function removeUrl(url: string) {
    const item = (model.value || []).find(i => i.url === url)
    
    model.value = (model.value || []).filter(i => i.url !== url)
    if (!item || !props.autoUpload || !mediaUpload) return

    mediaUpload.handleFileRemoval(item, () => {})
}

function clearItems() {
    if (!props.autoUpload || !mediaUpload) return

    const itemsToDelete = (model.value || []).filter(i => i.media_id || i.url)
    for (const item of itemsToDelete) {
        mediaUpload.handleFileRemoval(item, () => {})
    }
    model.value = [];
}

const aspectRatioStyle = computed(() => {
    const v = props.aspectRatio ?? '16:9'
    if (typeof v === 'number') return { aspectRatio: `${v}` }
    const normalized = v.replace(':', '/').replace(/\s+/g, '')
    // CSS wants space around '/'
    const css = normalized.includes('/') ? normalized.replace('/', ' / ') : normalized
    return { aspectRatio: css }
})

onMounted(() => {
    if (props.initialItems && props.initialItems.length) {
        const existing = model.value || []
        const toAdd = props.initialItems.filter(item => 
            !existing.some(it => it.url === item.url || it.media_id === item.media_id)
        )
        if (toAdd.length) model.value = [...existing, ...toAdd]
    } else if (props.initialUrls && props.initialUrls.length) {
        const existing = model.value || []
        const toAdd = props.initialUrls
            .filter(url => !existing.some(it => it.url === url))
            .map(url => ({ url }))
        if (toAdd.length) model.value = [...existing, ...toAdd]
    }
    
    if (!props.autoUpload || !mediaUpload || !props.refreshInterval) return
    
    for (const item of model.value || []) {
        if (!item.media_id || !item.isTemporary) continue
        mediaUpload.trackTemporaryMedia(item)
    }
    
    mediaUpload.startExpirationCheck(props.refreshInterval, (oldMediaId, newMediaId, newUrl) => {
        model.value = (model.value || []).map(item => {
            if (item.media_id === oldMediaId) {
                return {
                    ...item,
                    media_id: newMediaId,
                    url: newUrl,
                    uploadedAt: new Date(),
                    isTemporary: true,
                }
            }
            return item
        })
    })
})
</script>

<template>
    <UiFieldContainer flexable :size="'inline'" :invalid="props.invalid || Boolean(errorMsg)" :hint="props.hint"
        :error="errorMsg || props.error" :describedByIdBase="describedByBase"
        class="flex flex-coljustify-center text-center">
        <FileUpload.RootProvider :value="fileUploadApi" class="w-full flex flex-col gap-3">
            <FileUpload.Trigger class="w-full h-full se-border-dashed-6-6 rounded-lg"
                :class="{ 'pointer-events-none': isMaxed }">
                <FileUpload.Dropzone class="w-full h-full cursor-pointer relative" :class="{
                    'py-8 px-6 sm:py-12 sm:px-10 lg:py-16 lg:px-19': !(props.maxFiles === 1 && model && model.length),
                }" :disableClick="isMaxed || (props.maxFiles === 1)"
                    :allowDrop="!isMaxed || (props.maxFiles === 1)">
                    <div class="w-full flex flex-col items-center justify-center text-center gap-2"
                        v-if="!(props.maxFiles === 1 && model && model.length)">
                        <span class="text-lg font-semibold text-se-gray-900">Drag and drop your file or</span>
                        <UiButton :label="props.primaryText" variant="primary" :disabled="props.disabled">
                            <template #before_label>
                                <UiIcon name="upload" class="h-5 w-5" />
                            </template>
                        </UiButton>
                        <span v-if="props.secondaryText" class="max-w-[360px] text-center text-se-gray-700">{{ props.secondaryText }}</span>
                        <span v-if="counterText" class="text-se-gray-700 text-sm">{{ counterText }}</span>
                        <span v-if="isMaxed" class="text-se-gray-700 text-xs">Maximum reached</span>
                    </div>
                </FileUpload.Dropzone>
            </FileUpload.Trigger>
            <div v-if="props.maxFiles === 1 && model && model.length" class="flex flex-col gap-3"
                :style="aspectRatioStyle">
                <div v-for="item in model" :key="'item-' + (item.media_id || item.url || '')"
                    class="relative rounded-md border border-se-gray-300 overflow-hidden w-full h-full">
                    <div class="block">
                        <div class="w-full" :style="aspectRatioStyle">
                            <template v-if="item.url && (!item.file || isUrlLoaded(item.url))">
                                <img 
                                    :src="item.url" 
                                    class="object-cover w-full h-full"
                                    @load="() => loadedUrls.add(item.url!)"
                                />
                            </template>
                            <FileUpload.ItemGroup v-else-if="item.file && acceptedFiles.length > 0" class="w-full h-full">
                                <FileUpload.Item v-if="acceptedFiles[0]" :file="acceptedFiles[0]" class="w-full h-full">
                                    <FileUpload.ItemPreview type="image/*" class="block w-full h-full">
                                        <FileUpload.ItemPreviewImage class="object-cover w-full h-full" />
                                    </FileUpload.ItemPreview>
                                </FileUpload.Item>
                            </FileUpload.ItemGroup>
                            <img 
                                v-if="item.url && item.file && !isUrlLoaded(item.url)"
                                :src="item.url"
                                class="hidden"
                                @load="() => loadedUrls.add(item.url!)"
                            />
                        </div>
                    </div>
                    <UiButton type="button" class="absolute top-4 right-4 bg-se-white cursor-pointer"
                        @click="clearItems()" variant="outline" shape="square" no-py>
                        <template #before_label>
                            <UiIcon name="close" class="w-6 h-6" />
                        </template>
                    </UiButton>
                </div>
            </div>
            <FileUpload.ItemGroup>
                <div v-if="props.showThumbnails && props.maxFiles > 1 && model && model.length" class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div v-for="item in model.filter(i => i.url)" 
                        :key="'item-' + (item.media_id || item.url || '')"
                        class="relative rounded-md border border-se-gray-300 overflow-hidden">
                        <div class="block">
                            <div class="w-full" :style="aspectRatioStyle">
                                <template v-if="!item.file || isUrlLoaded(item.url!)">
                                    <img 
                                        :src="item.url" 
                                        class="object-cover w-full h-full"
                                        @load="() => loadedUrls.add(item.url!)"
                                    />
                                </template>
                                <template v-else-if="item.file">
                                    <FileUpload.Item :file="item.file" class="w-full h-full">
                                        <FileUpload.ItemPreview type="image/*" class="block">
                                            <div class="w-full" :style="aspectRatioStyle">
                                                <FileUpload.ItemPreviewImage class="object-cover w-full h-full" />
                                            </div>
                                        </FileUpload.ItemPreview>
                                    </FileUpload.Item>
                                </template>
                                <img 
                                    v-if="item.url && item.file && !isUrlLoaded(item.url)"
                                    :src="item.url"
                                    class="hidden"
                                    @load="() => loadedUrls.add(item.url!)"
                                />
                            </div>
                        </div>
                        <button type="button"
                            class="absolute top-2 right-2 bg-se-white/80 rounded-full p-1 border border-se-gray-300 cursor-pointer"
                            @click="removeUrl(item.url || '')">
                            <UiIcon name="close" class="w-2 h-2" />
                        </button>
                    </div>
                    <FileUpload.Item v-for="file in acceptedFiles.filter(f => !model.some(i => i.file === f))" 
                        :key="'file-' + file.name + file.size" 
                        :file="file"
                        class="relative rounded-md border border-se-gray-300 overflow-hidden">
                        <FileUpload.ItemPreview type="image/*" class="block">
                            <div class="w-full" :style="aspectRatioStyle">
                                <FileUpload.ItemPreviewImage class="object-cover w-full h-full" />
                            </div>
                        </FileUpload.ItemPreview>
                        <FileUpload.ItemDeleteTrigger
                            class="absolute top-2 right-2 bg-se-white/80 rounded-full p-1 border border-se-gray-300 cursor-pointer">
                            <UiIcon name="close" class="w-2 h-2" />
                        </FileUpload.ItemDeleteTrigger>
                    </FileUpload.Item>
                </div>
            </FileUpload.ItemGroup>
            <FileUpload.HiddenInput />

        </FileUpload.RootProvider>
    </UiFieldContainer>
</template>
